import { useContext } from "react";

import { useIntl } from "react-intl";

import ErrorBoundary from "@/components/error-boundary";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import {
    NUM_DISPLAY_RECOMMENDATIONS,
    ccpKeyToProductId
} from "../common/constants";
import { NavProductRecommendationsContext } from "../context/nav-product-recommendations-context";

import { ProductRecommendationsContainer } from "./product-recommendations-container";
import { SkeletonGrid } from "./skeleton-grid";

export interface GlobalNavProductRecommendationsSimpleProps {
    isMobile?: boolean;
}

const GlobalNavProductRecommendationsSimpleImplementation = ({
    isMobile = false
}: GlobalNavProductRecommendationsSimpleProps) => {
    const intl = useIntl();

    const { recommendations, isRecommendationLoading } = useContext(
        NavProductRecommendationsContext
    );

    const rawRecommendedProductIds = recommendations
        .map((ccpId) => ccpKeyToProductId[ccpId])
        .filter(Boolean);

    const recommendedTitle = intl.formatMessage({
        id: "GlobalNav.collections.featured.recommended.apps",
        defaultMessage: "Recommended based on your existing Atlassian apps"
    });

    const gwp7850GlobalNavChangesForTeam25Eu = checkFeatureFlagGate(
        featureFlags.gwp7850GlobalNavChangesForTeam25Eu
    );

    let recommendedProductIds = rawRecommendedProductIds;
    let fallbackIds = ["jira", "confluence", "jsm", "rovo"];
    if (gwp7850GlobalNavChangesForTeam25Eu) {
        recommendedProductIds = rawRecommendedProductIds.slice(0, 3);
        fallbackIds = ["jira", "confluence", "jsm"];
    }
    const fallbackTitle = intl.formatMessage({
        id: "GlobalNav.collections.featured.featuredapps",
        defaultMessage: "Featured apps"
    });

    return (
        <div className="global-nav-product-recommendations-3-max">
            {isRecommendationLoading ? (
                <SkeletonGrid numProducts={NUM_DISPLAY_RECOMMENDATIONS} />
            ) : (
                <ProductRecommendationsContainer
                    recommendedProductIds={
                        recommendedProductIds.length > 0
                            ? recommendedProductIds
                            : fallbackIds
                    }
                    isMobile={isMobile}
                    customTitle={
                        recommendedProductIds.length > 0
                            ? recommendedTitle
                            : fallbackTitle
                    }
                />
            )}
        </div>
    );
};

export const GlobalNavProductRecommendationsSimple = ({
    isMobile = false
}: GlobalNavProductRecommendationsSimpleProps) => {
    const { onGlobalNavProductRecommendationsFailed } = useContext(
        NavProductRecommendationsContext
    );

    return (
        <ErrorBoundary
            fallback={null}
            onError={onGlobalNavProductRecommendationsFailed}
        >
            <GlobalNavProductRecommendationsSimpleImplementation
                isMobile={isMobile}
            />
        </ErrorBoundary>
    );
};
